import React, { useEffect, useState } from 'react';
import { Pressable, Text, View, StyleSheet, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../../../styles/theme';
import { BottomSheet } from '../primitives/BottomSheet';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import { LibraryPickerPanel } from '../LibraryPickerPanel';
import { pickerSlotLabels } from '../libraryPicker';
import { rowOf } from '../constants';

interface Props {
  visible: boolean;
  entries: RecipeIndexEntry[];
  /** The slot being filled. */
  placingFor: number | null;
  /** The Monday bucket that slot lives in — the tapped section's, not the page's. */
  weekStart: string;
  onSelect: (recipeId: string) => void;
  onClose: () => void;
}

/**
 * Four fifths of the screen, not the 92% it used to take: the day strip stays
 * visible above it, so the week being planned never leaves the screen.
 */
const SHEET_SHARE = 0.8;

export function LibrarySheet({ visible, entries, placingFor, weekStart, onSelect, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { height } = useWindowDimensions();
  const { context } = pickerSlotLabels(placingFor, weekStart, t, i18n.language);

  // The panel mounts on first open (it folds the whole library for search) and
  // is re-keyed on every open, so each one starts from an empty search. It stays
  // mounted while the sheet slides away, so the sheet never empties mid-close.
  const [openCount, setOpenCount] = useState(0);
  useEffect(() => {
    if (visible) setOpenCount((n) => n + 1);
  }, [visible]);

  return (
    <BottomSheet
      visible={visible}
      onClose={onClose}
      snapPoint={Math.round(height * SHEET_SHARE)}
      scrollable={false}
    >
      <View style={styles.header} testID="library-sheet">
        <View style={styles.headerText}>
          <Text style={styles.title}>{t('thisWeek.library')}</Text>
          {!!context && (
            <View style={styles.contextPill} testID="library-context">
              <Feather name="calendar" size={12} color={Paprika.primary} />
              <Text style={styles.contextText}>{context}</Text>
            </View>
          )}
        </View>
        <Pressable
          style={styles.closeBtn}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close')}
          hitSlop={8}
        >
          <Feather name="x" size={15} color={Paprika.textSecondary} />
        </Pressable>
      </View>

      {openCount > 0 && (
        <LibraryPickerPanel
          key={openCount}
          entries={entries}
          mealIndex={placingFor !== null ? rowOf(placingFor) : null}
          placeLabel={null}
          autoFocus={false}
          onSelect={onSelect}
        />
      )}
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingTop: 6,
    paddingBottom: 12,
    gap: 10,
  },
  headerText: {
    flex: 1,
    alignItems: 'flex-start',
    gap: 8,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 24,
    lineHeight: 28,
    color: Paprika.textPrimary,
  },
  contextPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: Paprika.brandTint,
    borderRadius: 999,
    paddingHorizontal: 11,
    paddingVertical: 5,
  },
  contextText: {
    fontFamily: 'Inter-Medium',
    fontSize: 12.5,
    color: Paprika.primary,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Paprika.surfaceLow,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
